import {
    DestroyRef,
    Directive,
    ElementRef,
    NgZone,
    booleanAttribute,
    computed,
    effect,
    inject,
    input,
    isDevMode,
    linkedSignal,
    signal,
    untracked,
} from '@angular/core';

import { WidgetVisibilityConfig } from '../client-config/widget-visibility-config';

/**
 *
 * @experimental
 *
 * @whatItDoes Directive to prevent CLS by hiding elements until there preceding elements are visually stable
 *
 */
@Directive({
    selector: '[widgetVisibility]',
    standalone: true,
    host: { '[style.display]': 'hidden() ? "none":  null' },
})
export class WidgetVisibilityDirective {
    private config = inject(WidgetVisibilityConfig);

    private elRef = inject<ElementRef<HTMLElement>>(ElementRef);

    private destroyRef = inject(DestroyRef);
    private ngZone = inject(NgZone);

    /* @internal */
    parent: WidgetVisibilityDirective | null = null;

    /**
     * When applied in the template the element is automatically marked as ready
     */
    readonly forceVisible = input<boolean, never>(false, { transform: booleanAttribute, alias: 'widgetVisibility' });

    private readonly isRoot: boolean;

    private readonly children = signal<WidgetVisibilityDirective[]>([]);

    /**
     * @internal
     * The elements children ordered by there position in the document, as this is a computed it ensures that any time
     * a node is added or removed it will ensure they are correctly ordered.
     */
    readonly orderedChildren = computed(() =>
        this.children().sort((a, b) => {
            const pos = a.elRef.nativeElement.compareDocumentPosition(b.elRef.nativeElement);
            // b follows a -> a comes before b
            if (pos === Node.DOCUMENT_POSITION_FOLLOWING) {
                // -1 means a comes before b
                return -1;
            }
            // 1 means b comes before a
            return 1;
        }),
    );

    /**
     * A widget is visually stable when it has been marked as ready and all its children are visually stable.
     * Note that it is safe to display a widget that is not visibly stable, however displaying following elements may
     * cause layout shifts
     */
    private readonly visuallyStable: () => boolean = computed<boolean>(
        () => this.isReady() && this.children().every((child) => child.visuallyStable()),
    );

    private readonly isReady = linkedSignal<boolean>(() => this.isRoot || !this.config.enabled || this.forceVisible());

    private fallbackTimerExpired!: (() => boolean) | { (): boolean; set: (v: boolean) => void };

    readonly previousWidgetsReady = computed(() => {
        if (this.isRoot) {
            return true;
        }
        const orderedSiblings = this.parent ? this.parent.orderedChildren() : [];
        const widgetIndex = orderedSiblings.indexOf(this);
        for (let i = 0; i < widgetIndex; i++) {
            if (!orderedSiblings[i].visuallyStable()) {
                return false;
            }
        }
        return true;
    });

    protected readonly hidden = computed(() => {
        if (this.fallbackTimerExpired()) {
            return false;
        }
        if (!this.previousWidgetsReady()) {
            return true;
        }
        return !this.isReady();
    });

    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

    constructor() {
        try {
            /* @internal */
            this.parent = inject(WidgetVisibilityDirective, {
                skipSelf: true, // we want to check if there is a parent in this case
                optional: true, // we don't want to fail if we can't find a top level service -> this one becomes the parent one
            });
        } catch (e) {
            console.warn(`[WidgetVisibilityDirective]: Error injecting parent WidgetVisibility`);
        }
        this.isRoot = this.parent == null;
        // fallbackTimerExpired setup after isRoot is known
        if (this.isRoot) {
            this.fallbackTimerExpired = signal<boolean>(!this.config.enabled);
        } else {
            this.fallbackTimerExpired = computed<boolean>(() => this.parent!.fallbackTimerExpired());
        }

        if (this.isRoot) {
            if (this.config.enabled && this.config.markReadyFallback > 0) {
                this.ngZone.runOutsideAngular(() => {
                    return setTimeout(() => {
                        if (!this.visuallyStable()) {
                            if (isDevMode()) {
                                console.warn(
                                    `[WidgetVisibilityDirective]: Element was not marked as ready before ${this.config.markReadyFallback} fallback`,
                                );
                            }
                        }
                        (this.fallbackTimerExpired as any).set(true);
                    }, this.config.markReadyFallback);
                });
            }
        } else {
            this.parent!.registerChildNode(this);
            this.destroyRef.onDestroy(() => this.parent!.unregisterChildNode(this));
        }

        effect(() => {
            untracked(() => {
                if (isDevMode()) {
                    // causes memory leaks in dev mode
                    console.log('[WidgetVisibilityDirective]', {
                        _isReady: this.isReady(),
                        previous: this.previousWidgetsReady(),
                        hidden: this.hidden(),
                        parent: this.parent,
                        element: this.elementRef.nativeElement,
                    });
                }
            });
        });
    }

    /**
     * Marked as ready to be displayed
     */
    markReady() {
        this.isReady.set(true);
    }

    /* @internal */
    registerChildNode(child: WidgetVisibilityDirective): void {
        this.children.update((children) => [...children, child]);
    }

    /* @internal */
    unregisterChildNode(child: WidgetVisibilityDirective): void {
        this.children.update((children) => children.filter((el) => el !== child));
    }
}
